import { useState, useEffect } from "react";
import {
  Download,
  Calendar,
  TrendingUp,
  BarChart3,
  PieChart,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@b2b/ui-components";
import {
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { adminService } from "@b2b/services";

interface MetricCard {
  title: string;
  value: string;
  change: {
    value: number;
    isPositive: boolean;
  };
  icon: any;
}

const AdminReports = () => {
  const [dateRange, setDateRange] = useState("30days");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Data states
  const [metrics, setMetrics] = useState<MetricCard[]>([]);
  const [userGrowthData, setUserGrowthData] = useState<
    Array<{ period: string; buyers: number; suppliers: number }>
  >([]);
  const [categoryData, setCategoryData] = useState<
    Array<{ name: string; value: number; color: string }>
  >([]);

  // Format number with commas
  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  // Fetch all reports data
  const fetchReportsData = async () => {
    // Wait for token to be available before making API calls (prevents cancellation for Auth0 users)
    const { getAccessToken } = await import("@b2b/services");
    const token = await getAccessToken();
    if (!token) {
      console.log("⏳ AdminReports: Token not available yet, waiting...");
      // Retry after a short delay
      setTimeout(() => {
        fetchReportsData();
      }, 500);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log("📞 AdminReports: Fetching reports data...");

      const [
        metricsData,
        userGrowthDataResult,
        categoryDataResult,
      ] = await Promise.all([
        adminService.getReportsMetrics(dateRange),
        adminService.getUserGrowth(dateRange),
        adminService.getCategoryDistribution(),
      ]);

      // Transform metrics data
      const metricsCards: MetricCard[] = [
        {
          title: "Active Suppliers",
          value: formatNumber(metricsData.activeSuppliers),
          change: metricsData.changes.suppliers,
          icon: Users,
        },
        {
          title: "Active Buyers",
          value: formatNumber(metricsData.activeBuyers),
          change: metricsData.changes.buyers,
          icon: Users,
        },
      ];

      setMetrics(metricsCards);
      setUserGrowthData(userGrowthDataResult.data);
      setCategoryData(categoryDataResult.data);
      console.log("✅ AdminReports: Reports data fetched successfully");
    } catch (err: any) {
      console.error("❌ AdminReports: Error fetching reports data:", err);
      setError(err?.message || "Failed to load reports data");
      setToast({
        type: "error",
        message: err?.message || "Failed to load reports data",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when dateRange changes
  useEffect(() => {
    fetchReportsData();
  }, [dateRange]);

  // Auto-hide toast after a few seconds
  useEffect(() => {
    if (!toast) return;

    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toast]);

  // Handle export
  const handleExport = async () => {
    try {
      setToast({ type: "success", message: "Exporting reports..." });
      const blob = await adminService.exportReports(dateRange, "csv");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reports-${dateRange}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setToast({ type: "success", message: "Reports exported successfully!" });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err?.message || "Failed to export reports",
      });
    }
  };

  const totalCategoryValue = Math.max(
    categoryData.reduce((sum, item) => sum + item.value, 0),
    1
  );

  return (
    <div className="space-y-8 mt-5">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
          <button
            onClick={() => setToast(null)}
            className="ml-4 hover:opacity-80"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            Reports & Analytics
          </h1>
          <p className="text-muted-foreground">
            Comprehensive insights into your marketplace performance
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-48">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white ">
              <SelectItem value="7days" className="hover:bg-gray-100">
                Last 7 days
              </SelectItem>
              <SelectItem value="30days" className="hover:bg-gray-100">
                Last 30 days
              </SelectItem>
              <SelectItem value="90days" className="hover:bg-gray-100">
                Last 90 days
              </SelectItem>
              <SelectItem value="1year" className="hover:bg-gray-100">
                Last year
              </SelectItem>
            </SelectContent>
          </Select>

          <Button variant="primary" onClick={handleExport} disabled={loading}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg">Loading reports...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-16">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <Button onClick={fetchReportsData} variant="outline">
            Try again
          </Button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Metrics Cards */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
            {metrics.map((metric, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {metric.title}
                      </p>
                      <p className="text-3xl font-bold text-foreground">
                        {metric.value}
                      </p>
                      <div className="flex items-center space-x-1">
                        <TrendingUp
                          className={`h-4 w-4 ${
                            metric.change.isPositive
                              ? "text-green-600"
                              : "text-red-500"
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            metric.change.isPositive
                              ? "text-green-600"
                              : "text-red-500"
                          }`}
                        >
                          {metric.change.isPositive ? "+" : ""}
                          {metric.change.value}%
                        </span>
                        <span className="text-sm text-muted-foreground">
                          vs last period
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <metric.icon className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Live Analytics */}
          <div className="grid gap-5 lg:grid-cols-2">
            {userGrowthData.length > 0 && (
              <Card className="rounded-xl border border-gray-200 shadow-sm">
                <CardHeader className="pb-2 pt-4">
                  <CardTitle className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-orange-500" />
                    <span>User Growth</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={246}>
                    <RechartsBarChart data={userGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="period" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                          padding: "0.5rem",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="buyers" fill="#ea580c" name="Buyers" />
                      <Bar dataKey="suppliers" fill="#fb923c" name="Suppliers" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {categoryData.length > 0 && (
              <Card className="rounded-xl border border-gray-200 shadow-sm">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5 text-orange-500" />
                  <span>Top Product Categories</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="h-[246px] w-full">
                    <div className="flex h-full w-full flex-col items-center justify-center gap-5 md:flex-row md:items-center md:justify-between">
                      <div className="h-[176px] w-full max-w-[220px] md:h-[200px] md:max-w-[240px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={categoryData}
                              cx="50%"
                              cy="50%"
                              innerRadius={46}
                              outerRadius={78}
                              paddingAngle={2}
                              dataKey="value"
                              stroke="#ffffff"
                              strokeWidth={2}
                              labelLine={false}
                              label={false}
                              isAnimationActive={false}
                            >
                              {categoryData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "hsl(var(--card))",
                                border: "1px solid hsl(var(--border))",
                                borderRadius: "var(--radius)",
                              }}
                              formatter={(value) => {
                                const numericValue =
                                  typeof value === "number"
                                    ? value
                                    : Number(value) || 0;

                                return [
                                  `${numericValue.toLocaleString()} (${(
                                    (numericValue / totalCategoryValue) *
                                    100
                                  ).toFixed(1)}%)`,
                                  "Share",
                                ];
                              }}
                            />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="w-full max-w-[260px] space-y-1.5 pr-1">
                        {categoryData.map((category, index) => {
                          const percentage = (category.value / totalCategoryValue) * 100;

                          return (
                            <div key={`${category.name}-${index}`} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/40">
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                  style={{ backgroundColor: category.color }}
                                />
                                <span className="truncate text-sm text-foreground">
                                  {category.name}
                                </span>
                              </div>
                              <span className="ml-3 flex-shrink-0 text-sm font-medium text-muted-foreground">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
              </CardContent>
            </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminReports;
